import React, {useEffect, useState} from 'react';
import {Provider, useSelector} from 'react-redux';
import {NativeModules, Platform, LogBox} from 'react-native';

import {store, AppDispatch, useAppDispatch} from '../redux';
import {storage, StorageKeys} from '../shared/storage';
import {Navigation} from '../shared/routing/navigation';
import {ModalProvider} from '../shared/components/modal';
import {ActiveFormProvider} from '../shared/components/activeFormContext';
import {setThemeProperty} from '../redux/actions/runtime/theme';
import {Snackbar} from '../shared/components/snackbar';
import {getValueForKey} from '../shared/components/visuallyImpaired/wrapper';
import {themeSelector} from '../redux/selectors/runtime/theme';
import {VisImpStatus, VisImpSwitchColor} from '../shared/models/visuallyImpairedSettings';
import {HttpWrapper} from '../shared/httpWrapper';
import {DimensionsProvider} from '../shared/hooks/useDimensions';
import {ApiEnvironments, Settings} from '../shared/constants/config';

LogBox.ignoreLogs([
  /Authentication could not start because Fingerprint Scanner has no enrolled fingers/,
  /^Require cycle: /,
  /^It appears that you are using old version/,
]);

const Themed: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const dispatch = useAppDispatch();
  const themeSettings = useSelector(themeSelector());

  useEffect(() => {
    loadThemeSettings(dispatch).then(() => {
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    storage.getItem(StorageKeys.api).then((res) => {
      const env =
        process.env.NODE_ENV === 'development' || window.location.host.startsWith('test')
          ? ApiEnvironments.test
          : window.location.host.startsWith('stage')
          ? ApiEnvironments.stage
          : ApiEnvironments.prod;
      Settings.setSettings((res || env) as ApiEnvironments);
    });
  }, []);

  useEffect(() => {
    if (Platform.OS !== 'web') {
      return;
    }
    let themeName = 'default';

    if (themeSettings.visuallyImpaired === VisImpStatus.Active) {
      switch (themeSettings.switchColor) {
        case VisImpSwitchColor.Normal:
          themeName = 'visually-impaired';
          break;
        case VisImpSwitchColor.Invert:
          themeName = 'visually-impaired-inverted';
          break;
        case VisImpSwitchColor.Contrast:
          themeName = 'visually-impaired-contrast';
          break;
      }
    }

    document.body.dataset.themeName = themeName;
  }, [themeSettings.visuallyImpaired, themeSettings.switchColor]);

  if (loading) {
    return null;
  }
  return (
    <DimensionsProvider>
      <ModalProvider>
        <ActiveFormProvider>
          <Navigation />
          <Snackbar />
          <HttpWrapper />
        </ActiveFormProvider>
      </ModalProvider>
    </DimensionsProvider>
  );
};

const loadThemeSettings = async (dispatch: AppDispatch) => {
  const storageKeys = [
    StorageKeys.fontSize,
    StorageKeys.fontStyle,
    StorageKeys.letterSpacing,
    StorageKeys.switchColor,
    StorageKeys.switchImages,
    StorageKeys.visuallyImpaired,
  ];

  const promises = storageKeys.map((key) =>
    storage.getItem(key, false).then((value) => {
      if (value === null) {
        return;
      }

      // @ts-ignore
      dispatch(setThemeProperty(key, getValueForKey(key, value)));
    }),
  );

  try {
    await Promise.all(promises);
  } catch (e) {
    console.error(e);
  }
};

function App() {
  useEffect(() => {
    if (Platform.OS === 'android') {
      NativeModules.SplashScreenModule.hide();
    }
  }, []);

  return (
    <Provider store={store}>
      <Themed />
    </Provider>
  );
}

export default App;
